import React, {ReactNode, useCallback} from 'react';

import {Handler, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeaderProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {dropdownListHorizontalPadding} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {dropdownListDefaultRowHeight, DropdownVirtualList, ScrollHandler} from '../dropdownVirtualList';
import {
  SearchDropdownHeader,
  searchDropdownHeaderHeight,
  SearchDropdownHeaderOptionalProps,
} from './searchDropdownHeader';
import {computeIsNearBottomOfScollableList} from './searchDropdownHelpers';

/*
 * Constants.
 */

const defaultSearchDropdownWidth = 260;
const defaultMaxHeight = Math.round(dropdownListHorizontalPadding + dropdownListDefaultRowHeight * 8.5);

/*
 * Props.
 */

export interface SearchDropdownCommonProps {
  backButtonLabel?: ReactNode;
  onBackButtonClick?: Handler;
  subHeader?: {
    node: ReactNode;
    height: number;
  };
  defaultHighlightedIndex?: number;
  footer?: React.ReactNode;
  maxContentHeight?: number;
  isEditingWorkspace?: boolean;
}

export interface SearchDropdownProps<T>
  extends DropdownProps,
    DropdownHeaderProps,
    SearchDropdownHeaderOptionalProps,
    SearchDropdownCommonProps {
  /* The data to render. */
  data: ReadonlyArray<T>;
  /* Renders the rows in the dropdown. Each item from the data array is passed here. */
  renderRow: (item: T, index: number, data: ReadonlyArray<T>) => ReactNode;
  /* Returns the height for a given row. Needed for the virtual scroller. */
  findRowHeight: (item: T, index: number, data: ReadonlyArray<T>) => number;

  /* String from the search box. */
  query: string;
  /* Called when the query changes. */
  onQueryChange: (newQuery: string) => void;

  /*
   * Scroll event fired near the end of the scrollable area. Likely to be called multiple
   * times, since this is a scroll event handler. If using this to fetch more data, ensure
   * unnecessary requests are not being made.
   */
  onScrollNearBottom?: Handler;

  /*
   * Optional renderer. If a non-null value is returned, it's rendered instead
   * of the rows. Used to display any custom content (loading, error, initial
   * states, etc.)
   */
  renderOverlay?: Renderer;
}

export const SearchDropdown = <T,>(props: SearchDropdownProps<T>) => {
  const {onScrollNearBottom} = props;

  const onScroll: ScrollHandler = useCallback(
    (scrollContext) => {
      if (!onScrollNearBottom) {
        return;
      }

      // Only load more if we're near the bottom of the list.
      const isNearBottom = computeIsNearBottomOfScollableList(scrollContext);
      if (!isNearBottom) {
        return;
      }

      onScrollNearBottom();
    },
    [onScrollNearBottom],
  );

  return (
    <Dropdown {...props}>
      <SearchDropdownHeader
        {...props}
        value={props.query}
        onChange={props.onQueryChange}
        leftIcon={maybeRenderBackButton(props)}
      />
      {props.subHeader && props.subHeader.node}
      {renderContent(props, onScroll)}
      {props.footer}
    </Dropdown>
  );
};

function renderContent(props: SearchDropdownProps<any>, onScroll: ScrollHandler) {
  const overlay = props.renderOverlay && props.renderOverlay();
  if (overlay) {
    return overlay;
  }

  const subheaderHeight = props.subHeader ? props.subHeader.height : 0;

  return (
    <DropdownVirtualList
      width={props.width || defaultSearchDropdownWidth}
      maxHeight={
        props.maxContentHeight ??
        (props.height ? props.height - searchDropdownHeaderHeight - subheaderHeight : defaultMaxHeight)
      }
      rowCount={props.data.length}
      renderRow={(index) => props.renderRow(props.data[index], index, props.data)}
      getRowHeight={(index) => props.findRowHeight(props.data[index], index, props.data)}
      onRequestClose={props.onRequestClose}
      onScroll={onScroll}
      shouldSelectFirst={props.query.length > 0 ? true : undefined}
      defaultHighlightedIndex={props.defaultHighlightedIndex}
    />
  );
}

function maybeRenderBackButton<T>(props: SearchDropdownProps<T>) {
  if (!props.backButtonLabel || !props.onBackButtonClick) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="chevronLeftSmall"
          label={props.backButtonLabel}
          onClick={props.onBackButtonClick}
          size={VisualSizesEnum.SMALL}
          color={props.isEditingWorkspace ? theme.greys.shade70 : undefined}
          hoverColor={props.isEditingWorkspace ? theme.greys.shade80 : undefined}
        />
      )}
    </UserThemeConsumer>
  );
}
